import { ReleaseState } from './state';
import { releaseInitialState } from './initial-state';
import { ReleaseActions } from './action-creators';
import * as actions from './actions';
import { newRelease } from './new-release';
import { Release } from '@common/types/release/release';
import { SimpleRelease } from '@common/types/release/simple-release';

const getMinDate = (
  release?: Partial<Release>,
  releaseDate?: string
): string | undefined => {
  const original = Number(new Date(release?.originalReleaseDate || 0));
  const newDate = Number(new Date(releaseDate || 0));

  const min = Math.min(original, newDate);

  if (min) {
    return new Date(min).toISOString();
  }
};

export const releaseReducer = (
  state: ReleaseState = releaseInitialState,
  action: ReleaseActions
): ReleaseState => {
  switch (action.type) {
    case actions.EDIT_RELEASE: {
      return {
        ...state,
        release: {
          ...action.release,
        },
        lastSaved: {
          ...action.release,
        },
        updateStatus: !action.release?.id ? 'initial' : 'saved',
        validationErrors: [],
      };
    }

    case actions.CLEAN_RELEASE:
      return {
        ...state,
        release: newRelease(),
        missingSpotifyArtists: [],
        updateStatus: 'initial',
        lastSaved: undefined,
        locked: false,
        validationErrors: [],
      };

    case actions.CREATE_RELEASE_SUCCESS:
      return {
        ...state,
        release: {
          ...state.release,
          ...action.release,
          id: action.release.id,
        },
        updateStatus: 'saved',
      };

    case actions.UPDATE_RELEASE_SUCCESS:
      return {
        ...state,
        updateStatus: 'saved',
        lastSaved: action.release,
      };

    case actions.UPDATE_FIELD: {
      const releaseDate =
        action.field === 'releaseDate'
          ? (action.value as string)
          : state.release?.releaseDate;

      const newState: ReleaseState = {
        ...state,
        release: {
          ...state.release,
          [action.field]: action.value,
          status: state.release?.status,
          originalReleaseDate: state.release?.originalReleaseDate
            ? getMinDate(state.release, releaseDate)
            : releaseDate,
        },
        updateStatus:
          state.updateStatus === 'initial' ? state.updateStatus : 'modified',
      };

      return newState;
    }

    case actions.LOAD_RELEASE:
      return {
        ...state,
        release: undefined,
        missingSpotifyArtists: [],
        locked: false,
      };

    case actions.LOAD_RELEASE_SUCCESS:
      return {
        ...state,
        release: {
          ...action.release,
        },
        lastSaved: {
          ...action.release,
        },
        updateStatus: 'saved',
        locked: action.release.status
          ? action.release.status !== 'draft'
          : false,
        validationErrors: [],
      };

    case actions.UNLOCK_RELEASE:
      return {
        ...state,
        locked: false,
      };

    case actions.LIST_RELEASES:
      return {
        ...state,
        list: {
          [action.status]: [],
        },
      };

    case actions.LIST_RELEASES_SUCCESS:
      return {
        ...state,
        list: {
          [action.status]: action.releases,
        },
      };
    case actions.CANCEL_RELEASE_SUCCESS:
      return {
        ...state,
        list: {
          all: state?.list?.all?.map((i) =>
            i.id === action.releaseId ? { ...i, status: 'draft' } : i
          ),
        },
        featuring: state?.featuring?.map((i) =>
          i.id === action.releaseId ? { ...i, status: 'draft' } : i
        ),
      };
    case actions.CANCEL_PENDING_TAKEDOWN_SUCCESS:
      return {
        ...state,
        list: {
          all: state?.list?.all?.map((i) =>
            i.id === action.releaseId ? { ...i, status: action.status } : i
          ),
        },
        featuring: state?.featuring?.map((i) =>
          i.id === action.releaseId ? { ...i, status: action.status } : i
        ),
      };
    case actions.LIST_RELEASES_ARTIST_SUCCESS:
      return {
        ...state,
        listArtist: {
          ...state.listArtist,
          [action.artistId]: action.releases,
        },
      };

    case actions.LIST_RELEASES_DEACTIVATED_ARTIST_SUCCESS:
      return {
        ...state,
        listDeactivatedArtist: {
          id: action.artistId,
          releases: action.releases,
        },
      };

    case actions.LIST_MULTIPLE_ARTIST_RELEASES:
      return {
        ...state,
        artistMap: {},
      };

    case actions.LIST_MULTIPLE_ARTIST_RELEASES_SUCCESS:
      return {
        ...state,
        artistMap: action.releases.reduce((memo, release) => {
          if (!release.ownerId) {
            return memo;
          }
          if (!(release.ownerId in memo)) {
            memo[release.ownerId] = [];
          }

          memo[release.ownerId].push(release);

          return memo;
        }, {} as Record<string, SimpleRelease[]>),
      };

    case actions.LIST_FEATURING:
      return {
        ...state,
        featuring: [],
      };

    case actions.LIST_FEATURING_SUCCESS:
      return {
        ...state,
        featuring: action.releases,
      };

    case actions.CHANGE_ACTIVE_LIST:
      return {
        ...state,
        activeSection: action.section,
      };

    case actions.LIST_RELEASES_SUMMARY_SUCCESS:
      return {
        ...state,
        summary: action.summary,
      };

    case actions.PUBLISH_RELEASE_SUCCESS:
      return {
        ...state,
        release: {
          ...state.release,
          status: 'pending',
        },
        updateStatus: 'saved',
        missingSpotifyArtists: action.allArtists.filter(
          (a) => !a.externalIds?.spotify
        ),
        allReleaseArtists: action.allArtists,
      };

    case actions.PUBLISH_RELEASE_ERROR:
      return {
        ...state,
        validationErrors: action.errors ? JSON.parse(action.errors) : [],
      };

    case actions.VALIDATE_RELEASE_ARTISTS:
      return {
        ...state,
        missingSpotifyArtists: [],
      };

    case actions.VALIDATE_RELEASE_ARTISTS_SUCCESS:
      return {
        ...state,
        missingSpotifyArtists: action.artists.filter(
          (a) => !a.externalIds?.spotify
        ),
        allReleaseArtists: action.artists,
      };

    case actions.RESET_RELEASE_STORE:
      return { ...releaseInitialState };

    default:
      return state;
  }
};
